import { createContext, useMemo, useContext, useEffect, useState } from 'react';
import {
  Outlet,
  useSearchParams,
  useNavigate,
  useLocation,
  Location,
} from 'react-router-dom';
import { useRequest } from 'ahooks';

import { Representative, Spouse } from '~services';
import { useScrollTop } from '~hooks';
import { FullScreenLoading } from '~components';

import { ITransshipment, ITransshipmentContext } from './types';
import steps, { TStep } from './steps';
import * as amplitude from '@amplitude/analytics-browser';
import { TRepresentative } from '~services/Representative';

export const TransshipmentContext = createContext<ITransshipmentContext>(
  {} as ITransshipmentContext
);

export function useTransshipment() {
  return useContext(TransshipmentContext);
}

function TranshipmentProvider() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isSpouse =
    pathname.split('/').includes('conjuge') || !!searchParams.get('conjuge');

  const initialData = {
    id,
    type: isSpouse ? 'spouse' : 'representative',
    step: 'initial',
  } as ITransshipment;

  const [transshipment, setTransshipment] =
    useState<ITransshipment>(initialData);

  const request = useMemo(() => {
    if (isSpouse) {
      return Spouse.getData;
    }

    return Representative.getRepresentativeById;
  }, []);

  const {
    data: response,
    error,
    loading,
    refresh,
  } = useRequest<any, [string]>(request, {
    defaultParams: [id],
    onSuccess: handleSuccessLoading,
  });

  async function handleSuccessLoading() {
    const newTransshipment = {
      ...transshipment,
      data: response.data,
    };

    setTransshipment(newTransshipment);
    if (newTransshipment.type === 'representative') {
      const identifyEvent = new amplitude.Identify().set(
        'CNPJ',
        newTransshipment.data.company_document
      );
      amplitude.identify(identifyEvent);
    }

    nextStep(newTransshipment);
  }

  function nextStep(newTransshipment?: ITransshipment) {
    const currentState = steps[transshipment.step] as TStep;
    const nextStateId = currentState.next(newTransshipment || transshipment);
    const nextState = steps[nextStateId];

    setTransshipment((oldTransshipment) => ({
      ...oldTransshipment,
      ...newTransshipment,
      step: nextStateId,
    }));

    redirect(nextState.path);
  }

  function redirect(path: string) {
    const searchParams = new URLSearchParams(location.search);
    if (isSpouse && !searchParams.get('conjuge'))
      searchParams.append('conjuge', 'true');

    navigate({ pathname: path, search: searchParams.toString() });
  }

  function reset() {
    setTransshipment({
      ...initialData,
      step: isSpouse ? 'spouse-welcome-page' : 'representative-welcome-page',
    });
    refresh();
  }

  const isReadyToIdentify = (): boolean => {
    return (
      transshipment &&
      transshipment.type === 'representative' &&
      transshipment.data &&
      !!transshipment.data.email
    );
  };

  const dispatchRepresentativeEvent = (): void => {
    if (!isReadyToIdentify()) return;

    const representative = transshipment.data as TRepresentative;
    const identifyEvent = new amplitude.Identify().set(
      'email',
      representative.email
    );

    amplitude.identify(identifyEvent);
  };

  useEffect(() => {
    dispatchRepresentativeEvent();
  }, [transshipment]);

  useEffect(() => {
    if (error) throw error;
  }, [error]);

  useEffect(() => {
    const handleNavigation = (event) => {
      event.preventDefault();
      reset();
    };

    window.history.pushState(null, null, window.location.pathname);

    window.addEventListener('popstate', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
    };
  }, []);

  useScrollTop<Location>(location);

  return loading ||
    transshipment.data === undefined ||
    transshipment.step === 'initial' ? (
    <FullScreenLoading text="Carregando" />
  ) : (
    <TransshipmentContext.Provider value={{ ...transshipment, nextStep }}>
      <Outlet />
    </TransshipmentContext.Provider>
  );
}

export default TranshipmentProvider;
